/**
 * BajaPublicaPage — `/baja/:token` (FUERA de ProtectedRoute).
 *
 * Adonde lleva el link de baja que va al pie de cada mail de campaña. Quien la abre es el cliente
 * final del comercio, sin cuenta ni login.
 *
 * La baja NO se da al abrir la página: los antivirus y los previsualizadores de links abren las
 * URL de los mails solos, y si el GET diera la baja la daría un robot. Al cargar sólo se pregunta
 * de qué marca es el link; la baja es el botón (un POST), y recién ahí se confirma.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2, MailX } from "lucide-react";
import { activaApi } from "../api";

type Estado = "cargando" | "listo" | "enviando" | "hecho" | "invalido" | "error";

export default function BajaPublicaPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [estado, setEstado] = useState<Estado>("cargando");
  const [marca, setMarca] = useState("");

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const d = await activaApi.publicBajaInfo(token);
        if (vivo) { setMarca(d?.marca || ""); setEstado("listo"); }
      } catch (e: any) {
        if (vivo) setEstado(e?.status === 404 ? "invalido" : "error");
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  const darDeBaja = async () => {
    setEstado("enviando");
    try {
      const d = await activaApi.publicBaja(token);
      if (d?.marca) setMarca(d.marca);
      setEstado("hecho");
    } catch (e: any) {
      setEstado(e?.status === 404 ? "invalido" : "error");
    }
  };

  const deQuien = marca ? `de ${marca}` : "de este comercio";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <main className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm text-center">
        {estado === "cargando" && (
          <div className="flex flex-col items-center py-8 text-slate-400">
            <Loader2 size={28} className="animate-spin" />
            <p className="text-sm mt-3">Cargando…</p>
          </div>
        )}

        {(estado === "listo" || estado === "enviando") && (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center"><MailX size={22} /></div>
            <h1 className="text-lg font-bold text-slate-900 mt-4">¿Dejar de recibir mails {deQuien}?</h1>
            <p className="text-sm text-slate-500 mt-2">Si confirmás, no te vamos a mandar más mails de sus campañas.</p>
            <button
              type="button"
              onClick={darDeBaja}
              disabled={estado === "enviando"}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
            >
              {estado === "enviando" && <Loader2 size={16} className="animate-spin" />}
              Darme de baja
            </button>
          </>
        )}

        {estado === "hecho" && (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={22} /></div>
            <h1 className="text-lg font-bold text-slate-900 mt-4">Listo, ya te diste de baja</h1>
            <p className="text-sm text-slate-500 mt-2">No vas a recibir más mails {deQuien}.</p>
          </>
        )}

        {(estado === "invalido" || estado === "error") && (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center"><AlertCircle size={22} /></div>
            <p className="text-sm font-semibold text-slate-700 mt-4">
              {estado === "invalido" ? "Este link de baja no es válido." : "No pudimos registrar la baja."}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {estado === "invalido" ? "Revisá que hayas abierto el link completo del mail." : "Probá de nuevo en unos minutos."}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
